import { trackCartAddition } from '@/services/analytics.service'
import {
  fetchDiscountAmount as fetchDiscountAmountRequest,
  fetchUserCartOrder,
  syncUserCartOrder,
  createOrder
} from '@/services/shop.service'

function isLoggedIn (rootState) {
  return rootState?.auth?.status?.loggedIn === true
}

function hasCartData (state) {
  return state.cart.length > 0
}

const cartWrites = new WeakMap()
const cartVersions = new WeakMap()
const promoRequests = new WeakMap()

function cartSignature (items) {
  return JSON.stringify((items || []).map(item => [Number(item.id ?? item.product), Number(item.quantity)]).sort((a, b) => a[0] - b[0]))
}

function buildOrderPayload (getters, rootState, order = {}, cart) {
  const payload = {
    order_items: (cart || getters.cartProducts).map(item => ({ product: item.id, quantity: item.quantity }))
  }
  for (const field of ['fio', 'phone', 'email', 'delivery_company', 'delivery_office_token', 'terms_offer_accepted', 'pd_accepted', 'ads_accepted']) {
    if (order[field] !== undefined) payload[field] = order[field]
  }
  return payload
}

export default {
  // actions = methods
  async AddProductToCart ({ commit, state, getters, rootState, dispatch }, product) {
    commit('initialiseCart')
    if (isLoggedIn(rootState)) {
      await dispatch('refreshActiveOrderState')
    }
    const productId = product.id
    if (!rootState.catalog.products.some(item => item.id === productId)) {
      await dispatch('catalog/fetchProducts', undefined, { root: true })
    }
    product = rootState.catalog.products.find(item => item.id === productId)
    if (!product || product.stock <= 0) {
      return { added: false, requiresAuth: false }
    }

    const cartItem = state.cart.find(item => item.id === product.id)
    const before = Number(cartItem?.quantity || 0)
    if (before >= product.stock) return { added: false, requiresAuth: false }
    if (!cartItem) {
      commit('pushProductToCart', product.id)
    } else {
      commit('incrementItemQuantity', cartItem)
    }

    const added = Number(state.cart.find(item => item.id === product.id)?.quantity || 0) - before
    if (isLoggedIn(rootState)) await dispatch('syncCartOrder')
    if (added > 0) trackCartAddition(product, added)
    return { added: added > 0, requiresAuth: !isLoggedIn(rootState) }
  },
  async DecrementProductFromCart ({ commit, state, rootState, dispatch }, product) {
    commit('initialiseCart')
    if (isLoggedIn(rootState)) {
      await dispatch('refreshActiveOrderState')
    }
    const cartItem = state.cart.find(item => item.id === product.id)
    if (!cartItem) {
      return
    }
    if (cartItem.quantity > 1) {
      commit('decrementItemQuantity', cartItem)
    } else {
      commit('deleteItemFromCart', cartItem)
    }
    if (isLoggedIn(rootState)) {
      await dispatch('syncCartOrder')
    }
  },
  async DeleteItemFromCart ({ commit, state, rootState, dispatch }, product) {
    commit('initialiseCart')
    if (isLoggedIn(rootState)) {
      await dispatch('refreshActiveOrderState')
    }
    const cartItem = state.cart.find(item => item.id === product.id)
    if (!cartItem) {
      return
    }
    commit('deleteItemFromCart', cartItem)
    if (isLoggedIn(rootState)) {
      await dispatch('syncCartOrder')
    }
  },
  async AddCourseToCart ({ rootState }) {
    return { added: false, requiresAuth: !isLoggedIn(rootState) }
  },
  async DeleteCourseFromCart () {
    return null
  },
  async refreshActiveOrderState ({ state, rootState, commit, dispatch }) {
    if (!isLoggedIn(rootState)) {
      return null
    }
    const activeOrderId = Number(state.active_order_id || 0)
    if (!activeOrderId) {
      return null
    }
    try {
      const response = await fetchUserCartOrder(activeOrderId)
      const activeOrder = response?.data || null
      if (!activeOrder) {
        commit('setActiveOrderId', null)
        commit('setNewOrder', '')
        return null
      }
      if (Number(state.active_order_id) !== activeOrderId) return activeOrder
      if (activeOrder.is_paid) {
        try {
          const checkout = JSON.parse(localStorage.getItem('shop_checkout_request'))
          if (Number(checkout?.order_id) === activeOrderId) localStorage.removeItem('shop_checkout_request')
        } catch {}
        if (cartSignature(state.cart) === cartSignature(activeOrder.order_items)) {
          commit('clearCartState')
          await dispatch('syncCartOrder')
        } else {
          commit('setActiveOrderId', null)
          commit('setNewOrder', '')
        }
        return { is_paid: true, id: activeOrder.id }
      }
      commit('setNewOrder', activeOrder)
      return activeOrder
    } catch (error) {
      if (error?.response?.status === 404) {
        commit('setActiveOrderId', null)
        commit('setNewOrder', '')
        return null
      }
      throw error
    }
  },
  async syncCartOrder ({ commit, getters, rootState, state }, order = {}) {
    if (!isLoggedIn(rootState)) {
      throw new Error('Требуется авторизация')
    }
    const payload = buildOrderPayload(getters, rootState, order, state.cart)
    const version = (cartVersions.get(state) || 0) + 1
    cartVersions.set(state, version)
    const previous = cartWrites.get(state) || Promise.resolve()
    const pending = previous.catch(() => {}).then(() => syncUserCartOrder(payload))
    cartWrites.set(state, pending)
    const response = await pending
    const syncedOrder = response?.data || null
    if (cartVersions.get(state) === version) {
      commit('setNewOrder', hasCartData(state) ? syncedOrder : '')
      commit('setActiveOrderId', hasCartData(state) ? syncedOrder?.id : null)
    }
    return syncedOrder
  },
  async syncCartOrderAfterLogin ({ state, rootState, dispatch, commit }) {
    if (!isLoggedIn(rootState)) {
      return null
    }
    await dispatch('refreshActiveOrderState')
    if (!hasCartData(state)) {
      commit('setActiveOrderId', null)
      commit('setNewOrder', '')
      return null
    }
    return dispatch('syncCartOrder')
  },
  async fetchDiscountAmount ({ commit, state }, promocode) {
    const code = String(promocode || '').trim()
    const request = {}
    const owner = state || commit
    promoRequests.set(owner, request)
    commit('setPromocode', code)
    commit('setDiscountAmount', 0)
    if (!code) return
    try {
      const response = await fetchDiscountAmountRequest({ promocode: code })
      if (promoRequests.get(owner) === request) commit('setDiscountAmount', Number(response.data) || 0)
    } catch (e) {
      if (promoRequests.get(owner) === request) commit('setDiscountAmount', 0)
      throw e
    }
  },
  async fetchCreateOrder ({ commit, getters, state, rootState }, order = {}) {
    if (!isLoggedIn(rootState)) {
      throw new Error('Для оформления заказа войдите в аккаунт.')
    }
    const payload = buildOrderPayload(getters, rootState, order, state.cart)
    if (state.promocode) payload.promocode = state.promocode
    const signature = cartSignature(state.cart)
    const fingerprint = JSON.stringify({ user: rootState.auth.user?.id, payload })
    let saved
    try { saved = JSON.parse(localStorage.getItem('shop_checkout_request')) } catch {}
    const key = saved?.fingerprint === fingerprint ? saved.key : (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`)
    localStorage.setItem('shop_checkout_request', JSON.stringify({ fingerprint, key }))
    await (cartWrites.get(state) || Promise.resolve()).catch(() => {})
    const response = await createOrder(payload, key)
    if (response.data?.reservation_status === 'released') {
      const current = JSON.parse(localStorage.getItem('shop_checkout_request'))
      if (current?.key === key) localStorage.removeItem('shop_checkout_request')
      throw new Error('Резерв заказа завершён. Нажмите «Оплатить», чтобы оформить заказ заново.')
    }
    try {
      const current = JSON.parse(localStorage.getItem('shop_checkout_request'))
      if (current?.key === key) localStorage.setItem('shop_checkout_request', JSON.stringify({ ...current, order_id: response.data.id }))
    } catch {}
    if (signature === cartSignature(state.cart)) {
      commit('setNewOrder', response.data)
      commit('setActiveOrderId', response.data.id)
    }
    return response.data
  }
}
